import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Terminal, ArrowRight, Linkedin, Lock } from 'lucide-react';
import TerminalTyping from '../components/TerminalTyping';
import { SKILLS } from '../constants/data';

const Home = () => {
  const stats = useMemo(() => [
    { value: 'SMB→ENT', label: 'Full Spectrum', sublabel: 'All Market Segments', color: 'green' },
    { value: '7+', label: 'Years Pre-Sales', sublabel: 'Cybersecurity Focus', color: 'blue' },
    { value: 'Hybrid', label: 'Architecture', sublabel: 'Cloud + On-Prem Expert', color: 'red' }
  ], []);

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <div className="relative z-10 pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center py-20">
            {/* Terminal Window */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="bg-gray-900 border border-green-500/30 rounded-lg shadow-2xl">
                <div className="flex items-center justify-between px-4 py-2 bg-gray-800 rounded-t-lg border-b border-green-500/30">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full" />
                    <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                    <div className="w-3 h-3 bg-green-500 rounded-full" />
                  </div>
                  <span className="text-green-400 text-sm font-mono">terminal@tobias-harms</span>
                  <div />
                </div>
                <div className="p-6 font-mono text-left">
                  <div className="text-green-400 mb-2">
                    <span className="text-gray-500">$</span> whoami
                  </div>
                  <div className="text-white mb-4">
                    <TerminalTyping text="Senior Sales Engineer - Cybersecurity @ Vectra AI" speed={30} />
                  </div>
                  <div className="text-green-400 mb-2">
                    <span className="text-gray-500">$</span> cat /etc/specialization
                  </div>
                  <div className="text-white mb-4">
                    <TerminalTyping text="Hybrid/Cloud Security | Pre-Sales Strategy | XDR/NDR/CDR | AI-Driven Threat Detection" speed={25} />
                  </div>
                  <div className="text-green-400 mb-2">
                    <span className="text-gray-500">$</span> grep -i achievements /var/log/success
                  </div>
                  <div className="text-white">
                    <TerminalTyping text="90%+ POV Win Rate | OSCP & CRTO | Cloud & AI Security Expert" speed={20} />
                  </div>
                </div>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              <span className="text-green-400">TOBIAS</span> HARMS
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Cybersecurity <span className="text-green-400 font-semibold">Sales Engineer</span> with 7+ years driving 
              <span className="text-blue-400 font-semibold"> enterprise security transformations</span>. 
              Specializing in <span className="text-red-400 font-semibold">XDR/NDR/CDR solutions</span>, 
              POV execution, and bridging technical complexity with business value.
              <span className="text-sm text-gray-400 font-mono mt-2 block">
                From Germany to Miami • Multilingual: <span className="text-green-400">DE</span> | <span className="text-blue-400">EN</span> | <span className="text-yellow-400">ES</span>
              </span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
              <Link to="/projects" className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-black px-8 py-4 rounded-lg font-bold transition-all duration-300 flex items-center gap-2 shadow-lg shadow-green-500/25">
                <Terminal className="w-5 h-5" />
                EXPLORE SOLUTIONS
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a 
                href="https://linkedin.com/in/tobiasharms"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-green-500 text-green-400 hover:bg-green-500/10 px-8 py-4 rounded-lg font-bold transition-all duration-300 flex items-center gap-2"
              >
                <Linkedin className="w-4 h-4" />
                VIEW PROFILE
              </a>
            </div>
            
            <div className="text-sm text-gray-400 font-mono">
              Sr. Security Engineer at Vectra AI • Open to technical partnerships & speaking engagements
            </div>
          </div>
        </div>
      </div>

      {/* Skills Section */}
      <div className="relative z-10 py-20 bg-gradient-to-b from-transparent to-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-4 font-mono">
            <span className="text-green-400">&gt;</span> CORE.CAPABILITIES
          </h2>
          <p className="text-center text-gray-400 mb-16 font-mono">[ SALES ENGINEERING EXPERTISE MATRIX ]</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {SKILLS.map(({ name, icon: Icon, technologies, color }) => (
              <div key={name} className="group relative bg-gray-900/50 border border-green-500/30 p-6 rounded-lg hover:border-green-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/10 backdrop-blur-sm">
                <div className={`text-green-400 mb-4 p-3 rounded-lg bg-gradient-to-br ${color} bg-opacity-10`}>
                  <Icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-mono">{name}</h3>
                <ul className="space-y-2">
                  {technologies.map((tech) => (
                    <li key={tech} className="text-gray-300 flex items-center gap-2 text-sm">
                      <span className="text-green-400 font-mono">&gt;</span>
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative z-10 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map(({ value, label, sublabel, color }) => (
              <div key={label} className={`text-center p-6 bg-gray-900/30 border border-${color}-500/30 rounded-lg backdrop-blur-sm`}>
                <div className={`text-4xl font-bold text-${color}-400 mb-2 font-mono`}>{value}</div>
                <div className="text-gray-300 font-mono">{label}</div>
                <div className="text-sm text-gray-500 mt-2">{sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative z-10 py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-gray-900/50 to-black/50 border border-green-500/30 rounded-xl p-12 backdrop-blur-sm">
            <h2 className="text-4xl font-bold text-white mb-6 font-mono">
              <span className="text-green-400">&gt;</span> ESTABLISH.CONNECTION
            </h2>
            <p className="text-lg text-gray-300 mb-8 font-mono">
              [POV_COLLABORATION] || [TECHNICAL_PARTNERSHIPS] || [SECURITY_ADVISORY]
            </p>
            <Link to="/contact" className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-black px-8 py-4 rounded-lg font-bold transition-all duration-300 shadow-lg shadow-green-500/25">
              <Lock className="w-5 h-5" />
              INITIATE CONTACT
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;